import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import Drawer from "../Drawer";
import { LoaderSpinner } from "../LoaderSpinner";
import { ConfrimModal } from "../ConfrimModal";
import {
    BomUiLoadingKeys,
    fetchBundleBomUi,
    fetchPackagingBomUi,
    fetchProductBomUi,
    fetchSuppliesBomUi,
    getBomChangeLog,
    rollbackBomChanges,
    selectBomUi,
    setBomChangeHistoryOpen,
} from "../../slices/bomUiSlice";

const TAB_LABELS = {
    product: "Product BOM",
    bundle: "Bundle BOM",
    packaging: "Packaging",
    supplies: "Supplies",
};

const SOURCE_LABELS = {
    manual: "Manual edit",
    kip: "Kip (approved)",
    rollback: "Rollback",
};

const SOURCE_TONES = {
    manual: "bg-slate-100 text-slate-700",
    kip: "bg-sky-50 text-sky-700",
    rollback: "bg-emerald-50 text-emerald-700",
};

const COLUMNS = [
    { heading: "Source", width: "w-[12%]" },
    { heading: "By", width: "w-[8%]" },
    { heading: "When", width: "w-[15%]" },
    { heading: "Tab", width: "w-[10%]" },
    { heading: "SKU/Entity", width: "w-[12%]" },
    { heading: "Field", width: "w-[9%]" },
    { heading: "Change", width: "w-[17%]" },
    { heading: "Actions", width: "w-[17%]" },
];

const formatValue = (value) => (value == null || value === "" ? "—" : String(value));
const formatChangedAt = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const getRollbackRequest = (item, batchSize) => (
    batchSize > 1
        ? {
            kind: "batch",
            batchId: item.batchId,
            label: `Revert this entire batch of ${batchSize} changes?`,
        }
        : {
            kind: "change",
            changeLogId: item.id,
            label: `Revert ${item.sku || item.entityLabel || "this change"} ${item.field || "change"} back to ${formatValue(item.oldValue)}?`,
        }
);

const BomChangeHistoryDrawer = () => {
    const dispatch = useDispatch();
    const {
        changeHistoryOpen,
        bomChangeLog,
        bomChangeLogPagination,
        loadingKeys,
    } = useSelector(selectBomUi);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pendingRollback, setPendingRollback] = useState(null);
    const loading = loadingKeys.includes(BomUiLoadingKeys.getBomChangeLog);
    const rollingBack = loadingKeys.includes(BomUiLoadingKeys.rollbackBomChanges);

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    useEffect(() => {
        if (!changeHistoryOpen) return;
        dispatch(getBomChangeLog({
            page: currentPage,
            limit: 100,
            sku: debouncedSearchTerm,
            search: debouncedSearchTerm,
        }));
    }, [changeHistoryOpen, currentPage, debouncedSearchTerm, dispatch]);

    useEffect(() => {
        if (changeHistoryOpen) return;
        setCurrentPage(1);
        setSearchTerm("");
        setDebouncedSearchTerm("");
    }, [changeHistoryOpen]);

    const batchSizes = useMemo(() => {
        const sizes = new Map();
        for (const item of bomChangeLog || []) {
            sizes.set(item.batchId, (sizes.get(item.batchId) || 0) + 1);
        }
        return sizes;
    }, [bomChangeLog]);

    const refreshLog = useCallback(() => {
        dispatch(getBomChangeLog({
            page: currentPage,
            limit: 100,
            sku: debouncedSearchTerm,
            search: debouncedSearchTerm,
        }));
    }, [currentPage, debouncedSearchTerm, dispatch]);

    const submitRollback = useCallback(async () => {
        if (!pendingRollback) return;
        try {
            const result = await dispatch(
                rollbackBomChanges(
                    pendingRollback.kind === "batch"
                        ? { batchId: pendingRollback.batchId }
                        : { changeLogId: pendingRollback.changeLogId }
                )
            ).unwrap();
            toast.success(
                `Rolled back ${result?.rolledBackCount ?? 0} change${(result?.rolledBackCount ?? 0) === 1 ? "" : "s"}.`,
                { position: "bottom-right" }
            );
            setPendingRollback(null);
            await Promise.all([
                dispatch(fetchProductBomUi()),
                dispatch(fetchPackagingBomUi()),
                dispatch(fetchSuppliesBomUi()),
                dispatch(fetchBundleBomUi()),
            ]);
            refreshLog();
        } catch (error) {
            toast.error(typeof error === "string" ? error : "Could not roll back changes.", {
                position: "bottom-right",
            });
        }
    }, [dispatch, pendingRollback, refreshLog]);

    const pagination = bomChangeLogPagination || {};
    const totalPages = pagination.totalPages || 1;
    const isLastPage = pagination.isTheLastPage ?? currentPage >= totalPages;

    return (
        <>
        <Drawer
            isOpen={changeHistoryOpen}
            onClose={() => dispatch(setBomChangeHistoryOpen(false))}
            width="900px"
            mobileTopOffset
            showActionButtons={false}
            headerContent={
                <div className="flex min-w-0 items-center justify-between gap-2 pr-2">
                    <h2 className="min-w-0 text-xl font-bold text-gray-900">BOM Change History</h2>
                    <div className="flex shrink-0 items-center gap-1">
                        <button
                            type="button"
                            disabled={currentPage <= 1 || loading}
                            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                            className="rounded-md bg-white p-1.5 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ChevronLeftIcon className="h-3 w-3" />
                        </button>
                        <p className="min-w-[3rem] text-center text-xs text-gray-500">
                            {currentPage}/{totalPages}
                        </p>
                        <button
                            type="button"
                            disabled={isLastPage || loading}
                            onClick={() => setCurrentPage((page) => page + 1)}
                            className="rounded-md bg-white p-1.5 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ChevronRightIcon className="h-3 w-3" />
                        </button>
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-3 px-4 pb-6">
                <p className="mt-4 text-sm text-gray-500">
                    Every change to BOM components, supplies, packaging, and instructions is logged here.
                </p>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => {
                        const value = event.target.value;
                        setSearchTerm(value);
                        setCurrentPage(1);
                    }}
                    placeholder="Filter by SKU…"
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                />
                {loading ? (
                    <div className="flex justify-center py-10">
                        <LoaderSpinner width="48" height="48" border="4" colorSpinner="gray" />
                    </div>
                ) : !bomChangeLog.length ? (
                    <p className="py-10 text-center text-sm text-gray-500">No BOM changes logged yet.</p>
                ) : (
                    <>
                    <div className="hidden overflow-x-auto rounded-lg border border-gray-200 md:block">
                        <table className="w-full table-fixed text-left text-xs">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    {COLUMNS.map(({ heading, width }) => (
                                        <th
                                            key={heading}
                                            className={`whitespace-nowrap px-3 py-2 font-medium ${width}`}
                                        >
                                            {heading}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {bomChangeLog.map((item) => {
                                    const batchSize = batchSizes.get(item.batchId) || 1;
                                    return (
                                    <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${SOURCE_TONES[item.source] || SOURCE_TONES.manual}`}>
                                                {SOURCE_LABELS[item.source] || item.source || "—"}
                                            </span>
                                        </td>
                                        <td className="truncate px-3 py-2 text-gray-500">{item.changedBy || "—"}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-500">{formatChangedAt(item.changedAt)}</td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                                {TAB_LABELS[item.bomTab] || item.bomTab || "—"}
                                            </span>
                                        </td>
                                        <td className="truncate px-3 py-2 font-medium text-gray-800" title={item.sku || item.entityLabel || ""}>
                                            {item.sku || item.entityLabel || "—"}
                                        </td>
                                        <td className="truncate px-3 py-2 text-gray-700" title={item.field}>{item.field}</td>
                                        <td className="max-w-0 px-3 py-2 text-gray-700">
                                            <div className="break-words" title={`${formatValue(item.oldValue)} → ${formatValue(item.newValue)}`}>
                                                {formatValue(item.oldValue)} <span className="mx-1 text-gray-400">→</span> {formatValue(item.newValue)}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                type="button"
                                                disabled={rollingBack}
                                                onClick={() => setPendingRollback(getRollbackRequest(item, batchSize))}
                                                title={batchSize > 1
                                                    ? `Revert this batch of ${batchSize} changes`
                                                    : "Revert this change"}
                                                className="w-full truncate rounded-md border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {batchSize > 1 ? `Revert batch (${batchSize})` : "Revert"}
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="space-y-3 md:hidden">
                        {bomChangeLog.map((item) => {
                            const batchSize = batchSizes.get(item.batchId) || 1;
                            return (
                                <article key={item.id} className="rounded-lg border border-gray-200 p-3 shadow-sm">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${SOURCE_TONES[item.source] || SOURCE_TONES.manual}`}>
                                            {SOURCE_LABELS[item.source] || item.source || "—"}
                                        </span>
                                        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                            {TAB_LABELS[item.bomTab] || item.bomTab || "—"}
                                        </span>
                                    </div>
                                    <p className="mt-3 break-words text-base font-semibold text-gray-900">
                                        {item.sku || item.entityLabel || "—"}
                                    </p>
                                    <p className="mt-1 break-words text-sm font-medium text-gray-700">
                                        {item.field || "—"}
                                    </p>
                                    <p className="mt-2 break-words text-sm text-gray-700">
                                        {formatValue(item.oldValue)}
                                        <span className="mx-1 text-gray-400">→</span>
                                        {formatValue(item.newValue)}
                                    </p>
                                    <p className="mt-2 break-words text-xs text-gray-500">
                                        {item.changedBy || "—"} · {formatChangedAt(item.changedAt)}
                                    </p>
                                    <button
                                        type="button"
                                        disabled={rollingBack}
                                        onClick={() => setPendingRollback(getRollbackRequest(item, batchSize))}
                                        className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {batchSize > 1 ? `Revert batch (${batchSize})` : "Revert"}
                                    </button>
                                </article>
                            );
                        })}
                    </div>
                    </>
                )}
            </div>
        </Drawer>
        <ConfrimModal
            isOpen={Boolean(pendingRollback)}
            isLoading={rollingBack}
            title={pendingRollback?.label || "Revert changes?"}
            confirmButtonTitle="Yes, revert"
            cancelClick={() => {
                if (!rollingBack) setPendingRollback(null);
            }}
            submitClick={submitRollback}
            size="max-w-lg"
        />
        </>
    );
};

export default BomChangeHistoryDrawer;
